import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  TextInput,
  Modal,
  RefreshControl,
} from "react-native";
import {
  useNavigation,
  useRoute,
  RouteProp,
  useFocusEffect,
} from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import Svg, { Path, Circle } from "react-native-svg";
import { formatPct } from '../../utils/formatPercentage';
import { COLORS, FONTS } from "../../constants/config";
import { commitmentColor } from "../../utils/metricColors";
import {
  PageContainer,
  Navbar,
  Breadcrumb,
  KPIDistributionCard,
  EvidenceTable,
} from "../../components";
import TargetAchievementCard from "../../components/TargetAchievementCard";
import {
  useResponsive,
  useRoleNavigation,
  useAutoTranslate,
} from "../../hooks";
import { isPercentUnit } from "../../utils/targetGrid";
import { MainStackParamList } from "../../navigation/MainNavigator";
import { processesApi, subProcessesApi, goalsApi } from "../../services/apiService";
import { Process, SubProcess, KPIList } from "../../types";
import DeletionImpactModal from "../../components/DeletionImpactModal";
import DeletionApprovalBanner from "../../components/DeletionApprovalBanner";
import { useNotification } from "../../contexts/NotificationContext";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { getTextAlign } from "../../utils/rtl";
import { useYear } from "../../contexts/YearContext";

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import { PageHeader } from '@design/components/PageHeader';
type RouteProps = RouteProp<MainStackParamList, "ProcessDetail">;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({
  size = 16,
  color = tokens.colors.raw.gray["500"],
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M15 18L9 12L15 6"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const EditIcon = ({
  size = 16,
  color = tokens.colors.semantic.general.foreground,
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const ExportIcon = ({
  size = 16,
  color = tokens.colors.semantic.general.foreground,
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M17 8L12 3L7 8"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 3V15"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const PlusIcon = ({
  size = 16,
  color = tokens.colors.semantic.general.foreground,
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 5V19M5 12H19"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const ArrowRightIcon = ({
  size = 16,
  color = tokens.colors.semantic.general.foreground,
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M5 12H19M19 12L12 5M19 12L12 19"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// ============================================================
// Donut Chart Component
// ============================================================

interface DonutChartProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}

/**
 * Get color based on achievement percentage
 * - >= 100%: Green (#22C55E) - ممتاز
 * - >= 80% and < 100%: Orange (#F97316) - جيد
 * - < 80%: Red (#EF4444) - يحتاج تحسين
 */
const getAchievementColor = (percentage: number): string => {
  if (percentage >= 100) return tokens.colors.raw.green["500"]; // Green - Excellent
  if (percentage >= 80) return tokens.colors.raw.orange["500"]; // Orange - Good
  return tokens.colors.raw.red["500"]; // Red - Needs Improvement
};

const DonutChart = ({
  percentage,
  size = 168,
  strokeWidth = 12,
}: DonutChartProps) => {
  const { colors, isDark } = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Dynamic color based on achievement percentage
  const progressColor = commitmentColor(percentage);

  // Calculate dot position at the end of progress arc
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View
      style={{
        width: size,
        height: size,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["200"]}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot indicator at end of progress */}
        <Circle
          cx={dotX}
          cy={dotY}
          r={7.5}
          fill={progressColor}
          stroke={colors.card}
          strokeWidth={2}
        />
      </Svg>
      <View style={{ position: "absolute", alignItems: "center" }}>
        <Text
          style={{
            fontSize: tokens.typography["heading 3"]["font size"],
            fontFamily: FONTS.numbers,
            fontWeight: "500",
            color: colors.textPrimary,
          }}
        >
          %{percentage}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Progress Bar Component
// ============================================================

interface ProgressBarProps {
  current: number;
  total: number;
  color?: string;
}

const ProgressBar = ({
  current,
  total,
  color = "#2CA02C",
}: ProgressBarProps) => {
  const percentage = total > 0 ? Math.min((current / total) * 100, 100) : 0;

  return (
    <View style={progressStyles.container}>
      <View style={progressStyles.barBackground}>
        <View
          style={[
            progressStyles.barFill,
            { width: `${percentage}%`, backgroundColor: color },
          ]}
        />
      </View>
      <View style={progressStyles.dot} />
    </View>
  );
};

const getProgressStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: tokens.spacing.absolute["2"],
  },
  barBackground: {
    flex: 1,
    height: 8,
    backgroundColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-4"],
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: tokens.radii.absolute["radius-4"],
    backgroundColor: "#2CA02C",
  },
});

const progressStyles = getProgressStyles(true);

// ============================================================
// Main Screen Component
// ============================================================

export default function ProcessDetailScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { id } = route.params;
  const { isMobile, isTablet, isDesktop, isSmallMobile, horizontalCardWidth } =
    useResponsive();
  const { showError, showSuccess } = useNotification();
  const { navigateToHome, navigateToProcesses, isViewOnly } = useRoleNavigation();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { translate, translateOrg } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme colors for dark mode
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["100"],
    sectionBg: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["100"],
  };

  const [process, setProcess] = useState<Process | null>(null);
  const [subProcesses, setSubProcesses] = useState<SubProcess[]>([]);
  const [kpis, setKpis] = useState<KPIList[]>([]);
  const [loading, setLoading] = useState(true);

  const [refreshing, setRefreshing] = useState(false);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';
  const canApproveDeletion = ['org_executive', 'strategy_team', 'superuser'].includes(user?.profile?.user_type || '');
  const canManageEvidence = ['org_employee', 'coordinator', 'superuser'].includes(user?.profile?.user_type || '');
  const canViewAllEvidence = ['strategy_team', 'executive', 'performance_evaluator', 'evaluation_approver', 'superuser'].includes(user?.profile?.user_type || '');

  // Responsive
  // Tablet (640-1024px) is too narrow for the 3-card hero / multi-col layouts
  // used on this screen — treat it as small so cards stack vertically.
  const isSmallScreen = !isDesktop;
  const isMediumScreen = isTablet;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [processData, subProcessesData] = await Promise.all([
        processesApi.get(id),
        subProcessesApi.list({ process: id, page_size: 100 }),
      ]);

      setProcess(processData);
      setSubProcesses(subProcessesData.results || []);
      // Use KPIs from main response instead of separate API call
      setKpis(processData.kpis || []);
    } catch (err: any) {
      showError(t("processes.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData]),
  );

  // Refetch when year changes
  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const handleEdit = () => {
    navigation.navigate("AddProcess", { processId: id });
  };

  const handleDelete = () => {
    setShowDeletionImpact(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await processesApi.delete(id);
      if ((response as any)?.status === 202) {
        showSuccess(t('deletion.requestSent'));
      } else {
        showSuccess(t('processes.deleteSuccess'));
      }
      navigation.goBack();
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        navigation.goBack();
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(t('messages.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeletionImpact(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchData().finally(() => setRefreshing(false));
  };

  const handleExportPDF = () => {
    // TODO: Implement PDF export
    showError(t("common.exportFeatureComingSoon"));
  };

  const handleAddSubProcess = () => {
    navigation.navigate("AddSubProcess", { processId: id });
  };

  const handleAddKPI = () => {
    navigation.navigate("AddProcess", { processId: id, scrollToKpi: true });
  };

  const handleViewKPIDetails = (kpiId: number) => {
    navigation.navigate("KPIDetail", { id: kpiId });
  };

  // Main donut shows the process commitment rate (engine value, null → "--")
  const commitmentRate = process?.commitment_rate ?? null;

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={[styles.loadingText, { color: colors.textMuted }]}>
            {t("processes.loadingProcessData")}
          </Text>
        </View>
      </PageContainer>
    );
  }

  if (!process) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <Text style={[styles.errorText, { color: colors.textMuted }]}>
            {t("processes.notFound")}
          </Text>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.backButtonText}>{t("common.back")}</Text>
          </TouchableOpacity>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <View
          style={[styles.container, isSmallScreen && styles.containerSmall]}
        >
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: t("navigation.home") },
              { label: t("navigation.processes"), route: "Processes" },
              { label: t("processes.processDetails") },
            ]}
          />

          <PageHeader
            title={translate(process.name)}
            description={translate(process.description) || t("common.noDescription")}
            actions={
              <View style={styles.headerActions}>
                <Button
                  variant="secondary"
                  onPress={handleExportPDF}
                  trailingIcon={<ExportIcon size={16} color={themeColors.iconDefault} />}
                >
                  {t("common.exportPDF")}
                </Button>
                {!isViewOnly && (
                  <>
                    <Button variant="destructive" onPress={handleDelete}>
                      {t("common.delete")}
                    </Button>
                    <Button
                      variant="secondary"
                      onPress={handleEdit}
                      trailingIcon={<EditIcon size={16} color={themeColors.iconDefault} />}
                    >
                      {t("common.edit")}
                    </Button>
                  </>
                )}
              </View>
            }
          />

          {/* Pending Deletion Banner */}
          {(process as any).pending_deletion_info && (
            <DeletionApprovalBanner
              deletionInfo={(process as any).pending_deletion_info}
              canApprove={canApproveDeletion}
              canCancel={isOrgEmployee && (process as any).pending_deletion_info.requested_by_id === user?.id}
              onApproved={() => { showSuccess(t('deletion.approved')); navigation.goBack(); }}
              onRejected={() => { showSuccess(t('deletion.rejected')); fetchData(); }}
              onCancelled={() => { showSuccess(t('deletion.requestCancelled')); fetchData(); }}
              onViewImpact={() => setShowDeletionImpact(true)}
            />
          )}

          {/* Main Content Row - KPI Distribution, Donut Chart, and Basic Info */}
          <View
            style={[
              styles.mainContentRow,
              isSmallScreen && styles.mainContentRowSmall,
            ]}
          >
            {/* Left - Basic Information Section */}
            <View
              style={[
                styles.basicInfoSection,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
                isSmallScreen && styles.basicInfoSectionSmall,
              ]}
            >
              <Text
                style={[
                  styles.basicInfoTitle,
                  { color: themeColors.textPrimary },
                ]}
              >
                {t("common.basicInfo")}
              </Text>

              <View style={styles.infoGrid}>
                {/* Row 1 */}
                <View style={styles.infoRow}>
                  <View
                    style={[
                      styles.infoItem,
                      { backgroundColor: themeColors.sectionBg },
                    ]}
                  >
                    <Text
                      style={[
                        styles.infoLabel,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("common.classification")}
                    </Text>
                    <Text
                      style={[
                        styles.infoValue,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {t("common.main")}
                    </Text>
                  </View>
                </View>
                {/* Row 2 */}
                <View style={styles.infoRow}>
                  <View
                    style={[
                      styles.infoItem,
                      { backgroundColor: themeColors.sectionBg },
                    ]}
                  >
                    <Text
                      style={[
                        styles.infoLabel,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("initiatives.implementationResponsibility")}
                    </Text>
                    <Text
                      style={[
                        styles.infoValue,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {(process.organizational_units_details || []).map(u => translate(u.name_ar || u.name)).join('، ') || "-"}
                    </Text>
                  </View>
                </View>
              </View>
            </View>

            {/* Middle - Performance Indicators - Donut Chart */}
            <View
              style={[
                styles.donutSection,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
                isSmallScreen && styles.donutSectionSmall,
              ]}
            >
              <Text
                style={[
                  styles.sectionLabel,
                  { color: themeColors.textPrimary },
                ]}
              >
                {t("kpis.performanceIndicators")}
              </Text>
              {commitmentRate != null ? (
                <DonutChart
                  percentage={commitmentRate}
                  size={168}
                  strokeWidth={14}
                />
              ) : (
                <View style={{ width: 168, height: 168, justifyContent: 'center', alignItems: 'center' }}>
                  <Text style={{ fontSize: 36, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
                </View>
              )}
              {/* Legend Items */}
              <View style={styles.donutLegendItems}>
                <View style={styles.donutLegendRow}>
                  <View
                    style={[
                      styles.donutLegendDot,
                      { backgroundColor: tokens.colors.raw.green["500"] },
                    ]}
                  />
                  <Text
                    style={[
                      styles.donutLegendLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("kpis.excellent")} (≥100%)
                  </Text>
                </View>
                <View style={styles.donutLegendRow}>
                  <View
                    style={[
                      styles.donutLegendDot,
                      { backgroundColor: tokens.colors.raw.orange["500"] },
                    ]}
                  />
                  <Text
                    style={[
                      styles.donutLegendLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("kpis.good")} (≥80%)
                  </Text>
                </View>
                <View style={styles.donutLegendRow}>
                  <View
                    style={[
                      styles.donutLegendDot,
                      { backgroundColor: tokens.colors.raw.red["500"] },
                    ]}
                  />
                  <Text
                    style={[
                      styles.donutLegendLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("kpis.needsImprovement")}
                  </Text>
                </View>
              </View>
            </View>

            {/* Right - KPI Distribution Card */}
            <View style={[styles.kpiDistributionWrapper, isSmallScreen && styles.kpiDistributionWrapperSmall]}>
              <KPIDistributionCard distribution={process.kpi_distribution} />
            </View>
          </View>

          {/* Process Connections Card - ارتباطات العملية */}
          <View
            style={[
              styles.connectionsCard,
              {
                backgroundColor: themeColors.card,
                borderColor: themeColors.cardBorder,
              },
            ]}
          >
            <Text
              style={[
                styles.connectionsCardTitle,
                { color: themeColors.textPrimary },
              ]}
            >
              {t("processes.processConnections")}
            </Text>

            {/* KPIs Section */}
            <View
              style={[
                styles.kpisSection,
                { backgroundColor: themeColors.sectionBg },
              ]}
            >
              <View style={styles.kpisSectionHeader}>
                {!isViewOnly && (
                  <TouchableOpacity
                    style={[
                      styles.addKpiBtn,
                      { borderColor: themeColors.border },
                    ]}
                    onPress={handleAddKPI}
                  >
                    <Text
                      style={[
                        styles.addKpiBtnText,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {t("kpis.addKPIForProcess")}
                    </Text>
                    <PlusIcon size={13} color={themeColors.iconDefault} />
                  </TouchableOpacity>
                )}
                <Text
                  style={[
                    styles.kpisSectionTitle,
                    { color: themeColors.textPrimary },
                  ]}
                >
                  {t("kpis.performanceIndicators")}
                </Text>
              </View>

              <View style={styles.rtlWrapper}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={true}
                  contentContainerStyle={styles.horizontalCardsRow}
                  style={styles.horizontalScrollContainer}
                >
                  {kpis.length > 0 ? (
                    kpis.map((kpi) => {
                      // Commitment rate (نسبة الالتزام); null = nothing due yet → card renders —
                      const commitment = kpi.commitment_rate ?? null;
                      const progressColor = commitmentColor(commitment);

                      return (
                        <View
                          key={kpi.id}
                          style={[
                            styles.rtlCard,
                            { width: horizontalCardWidth },
                          ]}
                        >
                          <TargetAchievementCard
                            title={translate(kpi.name)}
                            achieved={kpi.latest_value || '0'}
                            target={kpi.target_value}
                            unit={isPercentUnit(kpi.unit) ? '%' : translate(kpi.unit)}
                            achievedColor={progressColor}
                            percentage={commitment}
                            onViewDetails={() => handleViewKPIDetails(kpi.id)}
                          />
                        </View>
                      );
                    })
                  ) : (
                    <View style={[styles.emptyStateContainer, styles.rtlCard]}>
                      <Text
                        style={[
                          styles.emptyStateText,
                          { color: themeColors.textMuted },
                        ]}
                      >
                        {t("kpis.noKPIs")}
                      </Text>
                    </View>
                  )}
                </ScrollView>
              </View>
            </View>

            {/* Strategic Goals Section */}
            <View
              style={[
                styles.strategicGoalsSection,
                { backgroundColor: themeColors.sectionBg },
              ]}
            >
              <View style={styles.strategicGoalsSectionHeader}>
                <Text
                  style={[
                    styles.strategicGoalsSectionTitle,
                    { color: themeColors.textPrimary },
                  ]}
                >
                  {t("goals.strategicGoals")}
                </Text>
              </View>

              {process.strategic_goals_details && process.strategic_goals_details.length > 0 ? (
                <View style={styles.strategicGoalsChipsRow}>
                  {process.strategic_goals_details.map((goal) => (
                    <TouchableOpacity
                      key={goal.id}
                      style={[
                        styles.strategicGoalChip,
                        {
                          backgroundColor: themeColors.card,
                          borderColor: themeColors.cardBorder,
                        },
                      ]}
                      onPress={() => {
                        navigation.navigate("GoalDetail", {
                          id: goal.id,
                          fromProcess: {
                            id: process.id,
                            name: process.name,
                          },
                        });
                      }}
                      activeOpacity={0.7}
                    >
                      <Text
                        style={[
                          styles.strategicGoalChipText,
                          { color: themeColors.textPrimary },
                        ]}
                      >
                        {translate(goal.title)}
                      </Text>
                      <ChevronLeftIcon size={14} color={themeColors.textPrimary} />
                    </TouchableOpacity>
                  ))}
                </View>
              ) : (
                <View style={styles.emptyStateContainer}>
                  <Text
                    style={[
                      styles.emptyStateText,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("processes.noStrategicGoal")}
                  </Text>
                </View>
              )}
            </View>

            {/* Sub-Processes Section */}
            <View
              style={[
                styles.subProcessesSection,
                { backgroundColor: themeColors.sectionBg },
              ]}
            >
              <View style={styles.subProcessesSectionHeader}>
                {!isViewOnly && (
                  <TouchableOpacity
                    style={[
                      styles.addSubProcessBtn,
                      { borderColor: themeColors.border },
                    ]}
                    onPress={handleAddSubProcess}
                  >
                    <Text
                      style={[
                        styles.addSubProcessBtnText,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {t("processes.addSubProcess")}
                    </Text>
                    <PlusIcon size={13} color={themeColors.iconDefault} />
                  </TouchableOpacity>
                )}
                <Text
                  style={[
                    styles.subProcessesSectionTitle,
                    { color: themeColors.textPrimary },
                  ]}
                >
                  {t("processes.subProcesses")}
                </Text>
              </View>

              {subProcesses.length > 0 ? (
                <View
                  style={[
                    styles.subProcessCardsGrid,
                    isSmallScreen && styles.subProcessCardsGridSmall,
                  ]}
                >
                  {subProcesses.map((subProcess) => {
                    // Commitment rate for the sub-process row (null → "--")
                    const subCommitment = subProcess.commitment_rate ?? null;
                    const progressPercentage = subCommitment == null
                      ? 0
                      : Math.min(subCommitment, 100);
                    // Color the bar by the real value (≥80 green / ≥50 orange /
                    // <50 red) — same scale every sibling card uses. Previously
                    // hard-coded green, so a 20% sub-process still read green.
                    const barColor = commitmentColor(subCommitment);

                    return (
                      <TouchableOpacity
                        key={subProcess.id}
                        style={[
                          styles.subProcessCardNew,
                          isSmallScreen && styles.subProcessCardNewSmall,
                          {
                            backgroundColor: themeColors.card,
                            borderColor: themeColors.cardBorder,
                          },
                        ]}
                        onPress={() =>
                          navigation.navigate("SubProcessDetail" as any, {
                            id: subProcess.id,
                          })
                        }
                        activeOpacity={0.7}
                      >
                        <View style={styles.subProcessCardContent}>
                          <Text
                            style={[
                              styles.subProcessCardTitle,
                              { color: themeColors.textPrimary },
                            ]}
                          >
                            {translate(subProcess.name)}
                          </Text>
                          <View style={styles.subProcessProgressRow}>
                            <View
                              style={[
                                styles.progressBadge,
                                { backgroundColor: themeColors.sectionBg },
                              ]}
                            >
                              <Text
                                style={[
                                  styles.progressBadgeText,
                                  { color: themeColors.textPrimary },
                                ]}
                              >
                                {subCommitment == null ? '--' : `${formatPct(progressPercentage)}%`}
                              </Text>
                            </View>
                            <View
                              style={[
                                styles.progressBarContainer,
                                { backgroundColor: themeColors.sectionBg },
                              ]}
                            >
                              <View
                                style={[
                                  styles.progressBarFill,
                                  { width: `${progressPercentage}%`, backgroundColor: barColor },
                                ]}
                              />
                              {subCommitment != null && progressPercentage > 0 && (
                                <View
                                  style={[
                                    styles.progressDot,
                                    {
                                      end: `${Math.max(progressPercentage - 5, 0)}%`,
                                      backgroundColor: barColor,
                                    },
                                  ]}
                                />
                              )}
                            </View>
                          </View>
                        </View>
                        <View style={styles.viewDetailsBtn}>
                          <Text
                            style={[
                              styles.viewDetailsBtnText,
                              { color: themeColors.textPrimary },
                            ]}
                          >
                            {t("common.viewDetails")}
                          </Text>
                          <ChevronLeftIcon
                            size={16}
                            color={themeColors.textPrimary}
                          />
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              ) : (
                <View style={styles.emptyStateContainer}>
                  <Text
                    style={[
                      styles.emptyStateText,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("processes.noSubProcesses")}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
        <EvidenceTable
          relatedType="process"
          filterRelatedId={id}
          title={t('evidence.sectionTitle')}
          canManage={canManageEvidence}
          showOrgColumn={canViewAllEvidence}
          onAddPress={() => navigation.navigate('AddEvidence', {
            relatedType: 'process', relatedId: id, contextLabel: t('processes.title'),
          })}
          onEditPress={(ev) => navigation.navigate('AddEvidence', {
            evidenceId: ev.id, relatedType: ev.related_type, relatedId: ev.related_id, contextLabel: t('processes.title'),
          })}
        />
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => setShowDeletionImpact(false)}
        onConfirm={confirmDelete}
        entityName={process?.name || ''}
        fetchImpact={async () => {
          const response = await processesApi.deletionImpact(id);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />

    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    padding: tokens.spacing.absolute["6"],
    paddingTop: tokens.spacing.absolute["4"],
  },
  containerSmall: {
    padding: tokens.spacing.absolute["4"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
  },
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
    marginBottom: tokens.spacing.absolute["4"],
  },
  backButton: {
    backgroundColor: COLORS.primary,
    paddingHorizontal: tokens.spacing.absolute["6"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  backButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: "row-reverse",
    alignItems: "center",
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  breadcrumbItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  breadcrumbText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },

  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: tokens.spacing.absolute["5"],
  },

  // Main Content Row
  mainContentRow: {
    flexDirection: "row-reverse",
    gap: tokens.spacing.absolute["6"],
    marginBottom: tokens.spacing.absolute["8"],
  },
  mainContentRowSmall: {
    flexDirection: "column",
  },

  // Donut Section
  donutSection: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["6"],
    alignItems: "center",
    width: 240,
  },
  donutSectionSmall: {
    width: "100%",
  },
  sectionLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    marginBottom: tokens.spacing.absolute["5"],
  },
  donutLegendItems: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["4"],
  },
  donutLegendRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: tokens.spacing.absolute["1_5"],
  },
  donutLegendDot: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute["radius-5"],
  },
  donutLegendLabel: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.regular,
  },

  // KPI Distribution Wrapper
  kpiDistributionWrapper: {
    flex: 1,
  },
  kpiDistributionWrapperSmall: {
    width: "100%",
  },

  // Basic Info Section
  basicInfoSection: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["6"],
  },
  basicInfoSectionSmall: {
    width: "100%",
  },
  basicInfoTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["5"],
  },
  infoGrid: {
    gap: tokens.spacing.absolute["4"],
  },
  infoRow: {
    flexDirection: "row-reverse",
    gap: tokens.spacing.absolute["6"],
  },
  infoItem: {
    flex: 1,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
  },
  infoLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
  },
  infoValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: "500",
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },

  // Process Connections Card (ارتباطات العملية)
  connectionsCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["6"],
    gap: tokens.spacing.absolute["4"],
    elevation: 2,
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  connectionsCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },

  // Section (Legacy)
  section: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["6"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: tokens.spacing.absolute["4"],
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  subSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },
  addSubProcessButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: tokens.radii.absolute["radius-8"],
    gap: tokens.spacing.absolute["1_5"],
  },
  addSubProcessText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },

  // Strategic Goals Section
  strategicGoalsSection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2_5"],
  },
  strategicGoalsSectionHeader: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  strategicGoalsSectionTitle: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  strategicGoalsChipsRow: {
    flexDirection: "row-reverse",
    flexWrap: "wrap",
    gap: tokens.spacing.absolute["2"],
    marginTop: tokens.spacing.absolute["2_5"],
  },
  strategicGoalChip: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: tokens.spacing.absolute["1_5"],
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
  },
  strategicGoalChipText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  strategicGoalsGrid: {
    flexDirection: "row-reverse",
    flexWrap: "wrap",
    gap: tokens.spacing.absolute["5"],
    marginTop: tokens.spacing.absolute["2_5"],
  },
  strategicGoalsGridSmall: {
    gap: tokens.spacing.absolute["3"],
  },
  strategicGoalCard: {
    width: "23.5%",
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["4"],
  },
  strategicGoalCardSmall: {
    width: "100%",
  },
  strategicGoalCardContent: {
    gap: tokens.spacing.absolute["4"],
  },
  strategicGoalCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },

  // Sub-Processes Section (Figma matching)
  subProcessesSection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2_5"],
  },
  subProcessesSectionHeader: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: tokens.spacing.absolute["5"],
  },
  subProcessesSectionTitle: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  addSubProcessBtn: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 5.5,
    borderRadius: tokens.radii.absolute["radius-8"],
    gap: tokens.spacing.absolute["1_5"],
    minHeight: 32,
    elevation: 2,
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  addSubProcessBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  subProcessCardsGrid: {
    flexDirection: "row-reverse",
    flexWrap: "wrap",
    gap: tokens.spacing.absolute["5"],
    marginTop: tokens.spacing.absolute["2_5"],
  },
  subProcessCardsGridSmall: {
    gap: tokens.spacing.absolute["3"],
  },
  subProcessCardNew: {
    width: "23.5%",
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["4"],
  },
  subProcessCardNewSmall: {
    width: "100%",
  },
  subProcessCardContent: {
    gap: tokens.spacing.absolute["4"],
  },
  subProcessCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  subProcessProgressRow: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: tokens.spacing.absolute["2"],
  },
  progressBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    borderRadius: 100,
  },
  progressBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: "center",
  },
  progressBarContainer: {
    flex: 1,
    height: 8,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    position: "relative",
    overflow: "visible",
    flexDirection: "row-reverse",
  },
  progressBarFill: {
    height: 8,
    backgroundColor: "#2CA02C",
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  progressDot: {
    position: "absolute",
    top: -3,
    width: 14,
    height: 14,
    borderRadius: tokens.radii.absolute["radius-7"],
    backgroundColor: "#2CA02C",
    borderWidth: 2,
    borderColor: "#FCFCFC",
  },
  viewDetailsBtn: {
    flexDirection: "row-reverse",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
  },
  viewDetailsBtnText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  emptyStateContainer: {
    padding: tokens.spacing.absolute["6"],
    alignItems: "center",
    justifyContent: "center",
  },
  emptyStateText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: "center",
  },

  // Cards Grid (Legacy)
  cardsGrid: {
    flexDirection: "row-reverse",
    flexWrap: "wrap",
    gap: tokens.spacing.absolute["4"],
  },
  cardsGridSmall: {
    gap: tokens.spacing.absolute["3"],
  },

  // Sub-Process Card (Legacy)
  subProcessCard: {
    width: "23%",
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["4"],
  },
  subProcessCardSmall: {
    width: "100%",
  },
  subProcessTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["3"],
    minHeight: 40,
  },
  subProcessProgress: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["3"],
  },
  progressText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  viewDetailsLink: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: tokens.spacing.absolute["1"],
  },
  viewDetailsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },

  // Horizontal scroll container for KPI cards
  horizontalScrollContainer: {
    flexGrow: 0,
    marginTop: tokens.spacing.absolute["2_5"],
  },
  // RTL wrapper - flips the entire ScrollView horizontally so cards start from right
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  // RTL card - flips each card back to normal orientation
  rtlCard: {
    transform: [{ scaleX: -1 }],
  },
  horizontalCardsRow: {
    flexDirection: "row",
    gap: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["1"],
    paddingBottom: tokens.spacing.absolute["3"],
  },

  // KPI Card
  kpiCard: {
    width: "48%",
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["4"],
  },
  kpiCardSmall: {
    width: "100%",
  },
  kpiTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["3"],
    minHeight: 40,
  },
  kpiProgress: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["3"],
  },
  kpiProgressText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    minWidth: 70,
    textAlign: getTextAlign(isRTL),
  },

  // KPIs Section (Figma matching - same as sub-processes)
  kpisSection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2_5"],
  },
  kpisSectionHeader: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: tokens.spacing.absolute["5"],
  },
  kpisSectionTitle: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  addKpiBtn: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 5.5,
    borderRadius: tokens.radii.absolute["radius-8"],
    gap: tokens.spacing.absolute["1_5"],
    minHeight: 32,
    elevation: 2,
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  addKpiBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },

  // KPI Card New (2 per row - wider than sub-process cards)
  kpiCardNew: {
    width: 288, // Fixed width (w-72)
    flexShrink: 0, // Prevent shrinking
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["4"],
  },
  kpiCardContent: {
    gap: tokens.spacing.absolute["4"],
  },
  kpiCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    minHeight: 40,
  },
  kpiProgressRow: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: tokens.spacing.absolute["2"],
  },
  kpiProgressBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    borderRadius: 100,
  },
  kpiProgressBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: "center",
  },
  kpiProgressBarContainer: {
    flex: 1,
    height: 8,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    position: "relative",
    overflow: "visible",
    flexDirection: "row-reverse",
  },
  kpiProgressBarFill: {
    height: 8,
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  kpiProgressDot: {
    position: "absolute",
    top: -3,
    width: 14,
    height: 14,
    borderRadius: tokens.radii.absolute["radius-7"],
    borderWidth: 2,
    borderColor: "#FCFCFC",
  },
  kpiViewDetailsBtn: {
    flexDirection: "row-reverse",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
  },
  kpiViewDetailsBtnText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacing.absolute["6"],
  },
  modalContent: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    width: "100%",
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    fontWeight: "600",
    color: tokens.colors.raw.gray["800"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
  },
  modalMessage: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },
  modalButtons: {
    flexDirection: "row",
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["4"],
  },
  modalCancelButton: {
    flex: 1,
    backgroundColor: tokens.colors.raw.gray["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: "center",
  },
  modalCancelButtonText: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
  },
  modalRejectButton: {
    flex: 1,
    backgroundColor: tokens.colors.raw.red["500"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: "center",
  },
  modalRejectButtonText: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.white,
  },
  rejectionReasonInput: {
    borderWidth: tokens.spacing.absolute["px"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    marginVertical: tokens.spacing.absolute["4"],
    minHeight: 100,
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    textAlignVertical: "top",
  },
});

